import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/custom/Dialog';
import { FormTemplate, formTemplateService } from '@/services/formTemplateService';
import { Search, Loader2, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface FormTemplateSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (template: FormTemplate) => void;
}

export const FormTemplateSelector: React.FC<FormTemplateSelectorProps> = ({
  open,
  onClose,
  onSelect
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);

  const limit = 20;
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const fetchFormTemplates = async (searchQuery: string, pageNum: number) => {
    setLoading(true);
    try {
      const result = await formTemplateService.getPaginatedFormTemplates(
        pageNum,
        limit,
        searchQuery
      );
      if (result && Array.isArray(result.data)) {
        setFormTemplates(result.data);
        setTotal(result.total || 0);
      } else {
        setFormTemplates([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Failed to fetch form templates', error);
      setFormTemplates([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFormTemplates(search, page);
    }
  }, [open, page]);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setPage(1); // Reset to first page on search
      fetchFormTemplates(value, 1);
    }, 500);
  };

  const handleTemplateClick = (template: FormTemplate) => {
    onSelect(template);
    onClose();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        className={`max-w-3xl max-h-[85vh] flex flex-col ${isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}
      >
        <DialogHeader>
          <DialogTitle>Select Form Template</DialogTitle>
          <DialogDescription className={isDark ? 'text-gray-400' : 'text-gray-500'}>
            Choose a form template to send to the customer.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col mt-4">
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            </div>
            <input
              type="text"
              placeholder="Search templates by name..."
              value={search}
              onChange={handleSearchChange}
              className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 focus:outline-none sm:text-sm ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          <div className="flex-1 overflow-y-auto pr-1">
            {loading ? (
              <div className="flex justify-center items-center h-32">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : formTemplates.length === 0 ? (
              <div className={`p-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>No form templates found.</p>
                {search && <p className="text-sm mt-1">Try adjusting your search criteria.</p>}
              </div>
            ) : (
              <ul className="space-y-2">
                {formTemplates.map((template) => (
                  <li
                    key={template.id}
                    onClick={() => handleTemplateClick(template)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      isDark
                        ? 'border-gray-700 bg-gray-800/50 hover:bg-gray-700/50'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="font-medium flex items-center gap-2">
                        {template.name}
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          ID: {template.id}
                        </span>
                      </h4>
                      <span
                        className={`text-xs whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                      >
                        {new Date(template.created_at).toLocaleDateString('en-GB')}
                      </span>
                    </div>
                    {template.description ? (
                      <p
                        className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                      >
                        {template.description}
                      </p>
                    ) : (
                      <p className={`text-sm italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        No description provided
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {totalPages > 1 && (
            <div
              className={`mt-4 pt-4 border-t flex items-center justify-between ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
            >
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}{' '}
                templates
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  className={`p-1 rounded-md ${
                    page === 1
                      ? 'opacity-50 cursor-not-allowed'
                      : isDark
                        ? 'hover:bg-gray-700'
                        : 'hover:bg-gray-100'
                  }`}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className={`text-sm px-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || loading}
                  className={`p-1 rounded-md ${
                    page === totalPages
                      ? 'opacity-50 cursor-not-allowed'
                      : isDark
                        ? 'hover:bg-gray-700'
                        : 'hover:bg-gray-100'
                  }`}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
